import { Icon } from '@iconify/react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ActionBox } from '@/Domain/components/Shared/ActionBox/ActionBox';
import { ConnectedList } from '@/Domain/components/Shared/ConnectedList/ConnectedList';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Snippet } from '@/Domain/components/Shared/Snippet/Snippet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ActionHelper } from '@/Domain/helper/Action.helper';
import { useMcpForm } from '@/Domain/hooks/Action/useMcpForm/useMcpForm';

export const McpIntegration = () => {
  const { t } = useTranslation('mcp_page');

  const { data, handleMutation } = useMcpForm();

  const claudeInstructionSteps = useMemo(
    () => [
      <Trans
        key="step_1"
        i18nKey="mcp_page:integration.claude.instruction.step_1"
        components={{
          external_link: (
            <a
              className="font-medium text-foreground cursor-pointer"
              href="https://docs.astral.sh/uv/getting-started/installation/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          code: <code />,
        }}
      />,
      t('integration.claude.instruction.step_2'),
      t('integration.claude.instruction.step_3'),
    ],
    [t],
  );

  const otherInstructionSteps = useMemo(
    () => [
      <Trans
        key="step_1"
        i18nKey="mcp_page:integration.other_apps.instruction.step_1"
        components={{
          external_link: (
            <a
              className="font-medium text-foreground cursor-pointer"
              href="https://docs.astral.sh/uv/getting-started/installation/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          code: <code />,
        }}
      />,
      <>{t('integration.other_apps.instruction.step_2')}</>,
    ],
    [t],
  );

  const onClickLink = () =>
    window.open('https://modelcontextprotocol.io/clients#feature-support-matrix', '_blank');

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-8">
        <ActionBox title={t('form.api_key_label')} description={t('form.api_key_sublabel')}>
          <Switch
            className="justify-self-end"
            checked={data?.api_key_required}
            onCheckedChange={(checked) => handleMutation({ api_key_required: checked })}
          />
        </ActionBox>

        <section>
          <Label>{t('integration.claude.title')}</Label>
          <ConnectedList items={claudeInstructionSteps} className="mt-1" />

          <Snippet
            code={ActionHelper.generateMcpIntegrationConfig(
              'claude',
              data?.name || '[your_name]',
              data?.api_key_required || false,
            )}
          />
        </section>

        <section>
          <Tooltip>
            <Label>{t('integration.other_apps.title')}</Label>
            <TooltipTrigger>
              <Icon
                icon="mingcute:question-line"
                className="ml-1 inline align-middle text-foreground-subtle"
              />
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('tooltip.available_mcp_applications')}</span>{' '}
              <span className="underline font-medium cursor-pointer" onClick={onClickLink}>
                {t('tooltip.available_mcp_applications_link')}
              </span>
            </TooltipContent>
          </Tooltip>
          <ConnectedList items={otherInstructionSteps} className="mt-1" />

          <Snippet
            code={ActionHelper.generateMcpIntegrationConfig(
              'other',
              data?.name || '[your_name]',
              data?.api_key_required || false,
            )}
          />
        </section>
      </div>
    </TooltipProvider>
  );
};
